// DEV-1050 cutover complete.
//
// This adapter previously hosted the V1 permissions evaluator
// (`evaluateSecurityForUser`), its data-injection plumbing
// (`hydrateSecurityAdapter` + `_hydrated*` module vars), and a suite of
// helpers (`projectCapsFromRole`, `collectionCapsFromRole`,
// `resolveOwningCommunityEntityGuidFromProject`, `getCommunityVisibility`,
// etc.). All of that has been retired: V2 is now the sole permissions
// engine. App.tsx derives `v2VisibleProjectGuids` and
// `authorizedCollectionGuids` directly from the V2 hook + API memberships;
// the per-call gate functions below consult the V2 capability cache
// (populated by `useV2ProjectRoles` mounted in App.tsx).
//
// What remains in this file:
//   - `isUserSystemAdmin` — fast sysadmin check (bypasses V2 evaluation)
//   - `canViewProject` / `canEditProjectContent` / `canAdminProject` — the
//     three synchronous gate functions consumed by 20+ call sites
//   - `hydrateSecurityAdapter` — slim shim retained so App.tsx can inject
//     the security-users list once on resolve; old multi-field signature
//     was removed
//   - `SecurityEvaluationOptions` — minimal interface retained for
//     backward-compatible optional override of the security-users list

import type { GUID } from "../types/security";
import {
  getV2ProjectCapsForUser,
  v2CanViewProject,
  v2CanEditProjectContent,
  v2CanAdminProject,
} from "./v2/useV2ProjectRoles";

// ── Module-scope hydrated security-users list ──────────────────
// Populated once from App.tsx after the security API hook resolves.
// `isUserSystemAdmin` reads from here when callers don't pass an explicit
// list. The previous DEV-932 Phase 6 multi-field hydration is gone with
// V1; only the security-users axis remains.
let _hydratedSecurityUsers: any[] | null = null;

/**
 * Inject the security-users list into the adapter's module scope so the
 * sysadmin fast-path resolves without callers threading the list through.
 * Slimmed signature compared to the legacy V1 version: only
 * `securityUsers` is honored now. App.tsx is the canonical caller.
 */
export function hydrateSecurityAdapter(data: { securityUsers?: any[] }): void {
  if (data.securityUsers?.length) _hydratedSecurityUsers = data.securityUsers;
}

/**
 * Optional per-call overrides. Most callers omit this and rely on the
 * module-scope hydrated list. Retained for backward compatibility — none
 * of the current 20+ call sites pass it, but the optional third argument
 * keeps signatures stable.
 */
export interface SecurityEvaluationOptions {
  /** Override the security-users list used by the sysadmin check. */
  securityUserList?: any[];
}

/**
 * Fast sysadmin check. Sysadmins bypass V2 capability evaluation and are
 * granted full access on every project. Performance-sensitive — called
 * inside the three gate functions for every permission check.
 */
/**
 * DEV-1414: resolve a hydrated security-user row by guid (case-insensitive).
 * Used by surfaces that need identity context (email, avatar) outside the
 * useApiSecurityData hook tree — e.g. the Action Center tray resolving a
 * membership requester LIVE at render (PII is never denormalized into
 * immutable workflow_requests rows). Returns null pre-hydration.
 */
export function getHydratedSecurityUser(user_guid: GUID): any | null {
  const g = String(user_guid || '').toUpperCase();
  if (!g) return null;
  return (
    (_hydratedSecurityUsers || []).find(
      (u: any) => String(u.user_guid || u.userGuid || '').toUpperCase() === g,
    ) ?? null
  );
}

export function isUserSystemAdmin(user_guid: GUID, userList?: any[]): boolean {
  const g = String(user_guid).toUpperCase();
  const users = userList && userList.length > 0 ? userList : _hydratedSecurityUsers ?? [];
  return (users || []).some(
    (u: any) => String(u.user_guid || u.userGuid || '').toUpperCase() === g
      && (u.is_system_admin === true || u.isSystemAdmin === true)
  );
}

/**
 * Can the user view this project?
 *
 * Resolution order:
 *   1. Sysadmin → true (short-circuit).
 *   2. V2 capability cache has resolved for this user → defer to V2.
 *      `v2CanViewProject` returns true when the user holds any view-floor
 *      capability (`about:view`) on the project.
 *   3. V2 hasn't resolved yet (cold-start, ~500ms) → false. Conservative
 *      falsy access per the DEV-932 Phase 6 / DEV-1050 cutover policy:
 *      better to briefly under-grant than to leak access via stale data.
 */
export function canViewProject(
  user_guid: GUID,
  project_guid: GUID,
  opts: SecurityEvaluationOptions = {},
): boolean {
  if (isUserSystemAdmin(user_guid, opts.securityUserList)) return true;
  const userV2Map = getV2ProjectCapsForUser(user_guid);
  if (userV2Map === undefined) return false;
  const projectCaps = userV2Map.get(String(project_guid).toUpperCase());
  return projectCaps ? v2CanViewProject(projectCaps) : false;
}

/**
 * Can the user edit project content? V1's contributor-or-higher equivalent:
 * holding any per-area edit capability OR `project:admin` (which transitively
 * grants every edit).
 */
export function canEditProjectContent(
  user_guid: GUID,
  project_guid: GUID,
  opts: SecurityEvaluationOptions = {},
): boolean {
  if (isUserSystemAdmin(user_guid, opts.securityUserList)) return true;
  const userV2Map = getV2ProjectCapsForUser(user_guid);
  if (userV2Map === undefined) return false;
  const projectCaps = userV2Map.get(String(project_guid).toUpperCase());
  return projectCaps ? v2CanEditProjectContent(projectCaps) : false;
}

/**
 * Can the user administer this project? V1's owner_admin equivalent:
 * holds `project:admin` OR `project:settings`.
 */
export function canAdminProject(
  user_guid: GUID,
  project_guid: GUID,
  opts: SecurityEvaluationOptions = {},
): boolean {
  if (isUserSystemAdmin(user_guid, opts.securityUserList)) return true;
  const userV2Map = getV2ProjectCapsForUser(user_guid);
  if (userV2Map === undefined) return false;
  const projectCaps = userV2Map.get(String(project_guid).toUpperCase());
  return projectCaps ? v2CanAdminProject(projectCaps) : false;
}
